import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from "prop-types";
import { TimePicker } from 'antd';
import moment from 'moment';
import _ from "lodash";
// import TimePicker from './TimePicker';

export default function AntDTimePicker(props) {
  const {
    isMilitaryTime = false,
    value,
    onChange,
    inputStyle,
    computeDisabledHours,
    computeDisabledMinutes,
    disabled,
    placeholder,
    style,
    size = "default",
    onError,
    className,
    getPopupContainer,
    dataTestId,
    // ... other Ant Design TimePicker props you might need
  } = props;

  // Ensure pickerValue is always a moment object or null
  const [pickerValue, setPickerValue] = useState(null); 

  useEffect(() => {
    // Attempt to parse the 'value' prop into a moment object
    const parsedValue = 
      value && moment(value, isMilitaryTime ? 'HH:mm' : 'h:mm A', true).isValid() 
        ? moment(value, isMilitaryTime ? 'HH:mm' : 'h:mm A', true) 
        : null;

    setPickerValue(parsedValue); 
  }, [value, isMilitaryTime]);

  const getDisabledHours = useCallback(() => {
    if (!pickerValue) return [];
    return computeDisabledHours(pickerValue);
  }, [computeDisabledHours, pickerValue]);

  const getDisabledMinutes = useCallback(
    (selectedHour) => {
      if (!pickerValue) return [];
      return computeDisabledMinutes(pickerValue, selectedHour);
    },
    [computeDisabledMinutes, pickerValue]
  );

  const formatTimeString = (timeStr) => {
    const digits = timeStr?.replace(/\D/g, '');

    if (digits.length === 4) {
      const hours = digits.slice(0, 2);
      const minutes = digits.slice(2);
      return `${hours}:${minutes}`;
    }

    if (digits.length === 3) {
      const hours = digits.slice(0, 1);
      const minutes = digits.slice(1);
      return `${hours.padStart(2, '0')}:${minutes}`;
    }

    if (digits.length === 2) {
      return `00:${digits}`;
    }

    if (digits.length === 1) {
      return `00:0${digits}`;
    }

    return timeStr;
  };

  const parseAndSetTime = (timeString) => {
    if (!timeString) return false;

    if (!isMilitaryTime) {
      const timeStr = timeString.toLowerCase();
      const isPM = timeStr.includes('pm');
      const isAM = timeStr.includes('am');
      
      const digits = timeStr.replace(/[^0-9]/g, '');
      
      if (digits.length > 0) {
        let hours, minutes;
        
        if (digits.length === 4) {
          hours = parseInt(digits.slice(0, 2), 10);
          minutes = parseInt(digits.slice(2), 10);
        } else if (digits.length === 3) {
          hours = parseInt(digits.slice(0, 1), 10);
          minutes = parseInt(digits.slice(1), 10);
        } else if (digits.length === 2) {
          hours = parseInt(digits, 10);
          minutes = 0;
        } else {
          hours = parseInt(digits, 10);
          minutes = 0;
        }

        if (hours < 1 || hours > 12 || minutes < 0 || minutes > 59) {
          console.error('Invalid time format:', timeString);
          _.isFunction(onError) && onError('Invalid time format');
          return false;
        }
        
        if (isPM && hours < 12) {
          hours += 12;
        }
        if (isAM && hours === 12) {
          hours = 0;
        }
        
        const parsedTime = moment().hours(hours).minutes(minutes);
        if (parsedTime.isValid()) {
          setPickerValue(parsedTime);
          _.isFunction(onChange) && onChange(parsedTime);
          return true;
        }
      }
    } else {
    const formattedTimeString = formatTimeString(timeString);
    const parsedTime = moment(formattedTimeString, 'HH:mm', true);

    if (parsedTime.isValid()) {
      const hours = parsedTime.hours();
      const minutes = parsedTime.minutes();

      if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
        setPickerValue(parsedTime);
        _.isFunction(onChange) && onChange(parsedTime);
        return true;
      }
      }
    }

    console.error('Invalid time format:', timeString);
    _.isFunction(onError) && onError('Invalid time format');
    return false;
  };


  const handleTimeChange = (time, timeString) => {
    if (time === null) { // Check if time is null (when clear is clicked)
        setPickerValue(null); // Clear the selected time
        _.isFunction(onChange) && onChange(time);
      return;
    }

    if (typeof timeString === 'string') {
      parseAndSetTime(timeString);
    }
  };

  const handleKeyDown = (e) => {
    const target = e.target;

    // Handle immediate 4-digit formatting
    setTimeout(() => {
      if (target && target.value) {
        const inputValue = target.value;
        const digits = inputValue.replace(/\D/g, '');

        if (digits.length === 4) {
          parseAndSetTime(digits);
        }
      }
    }, 0);

    // Handle Enter key for any length
    if (e.key === 'Enter') {
      const inputValue = e.target.value;
      const digits = inputValue.replace(/\D/g, '');

      if (digits.length > 0 && digits.length <= 4) {
        parseAndSetTime(digits);
        }
    }
};


  return (
    <TimePicker
      data-testid={`${dataTestId}`}
      value={pickerValue}
      onChange={handleTimeChange}
      onKeyDown={handleKeyDown}
      format={isMilitaryTime ? 'HH:mm' : 'h:mm A'}
      use12Hours={!isMilitaryTime}
      disabled={disabled}
      placeholder={placeholder}
      style={{ ...style, width: '100%' }} // Adjust width as needed
      size={size}
      disabledHours={getDisabledHours}
      disabledMinutes={getDisabledMinutes}
      className={className}
      getPopupContainer={getPopupContainer || ((trigger) => trigger.parentNode)} 
      // ... other Ant Design TimePicker props
    />
  );
}

AntDTimePicker.propTypes = {
  isMilitaryTime: PropTypes.bool,
  value: PropTypes.oneOfType([
    PropTypes.object, // Allow moment object or compatible object
    PropTypes.string, // Allow string representation of time
  ]),
  onChange: PropTypes.func,
  inputStyle: PropTypes.object, 
  computeDisabledHours: PropTypes.func,
  computeDisabledMinutes: PropTypes.func,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  style: PropTypes.object,
  size: PropTypes.oneOf(['large', 'small']),
  onError: PropTypes.func,
  className: PropTypes.string,
  getPopupContainer: PropTypes.func,
  // ... PropTypes for other Ant Design TimePicker props you might use
};

AntDTimePicker.defaultProps = {
  isMilitaryTime: false,
  value: null, 
  onChange: () => {},
  inputStyle: null, 
  computeDisabledHours: () => [],
  computeDisabledMinutes: () => [],
  disabled: false,
  placeholder: "Time",
  style: null,
  size: 'small',
  onError: () => {},
  className: '',
  getPopupContainer: null,
  // ... defaultProps for other Ant Design TimePicker props
};